import { Typography } from '@euler/app/components/typography';
import { applyOrderFacadeItemResult } from '@euler/app/flows/order/functions/applyOrderFacadeItemResult';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { useFacadeInspectionContext } from '@euler/app/flows/order/pages/FacadeInspectionScreen/components/FacadeInspectionContext';
import { mediaStagedToMediaInfo } from '@euler/app/flows/task/functions/task-detail/util';
import { FacadeMediaList } from '@euler/app/flows/task/pages/pre-inspection/components/FacadeInspectionMediaListView';
import { FacadeMediaInfo } from '@euler/app/flows/task/pages/pre-inspection/functions/types';
import { StatusColors } from '@euler/components/Colors';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SiteInspectionStaged } from '@euler/model/task-detail';
import { safeMarkDevFlag, withConfirmation } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useMemo } from 'react';
import { ActivityIndicator, StyleProp, View, ViewStyle } from 'react-native';

const kFakeStatus = safeMarkDevFlag(false);

export const WiredFacadeMediaListView = memo(
  ({
    siteInspection,
    style,
    onRetryUpload,
    onAdd,
  }: {
    siteInspection: SiteInspectionStaged;
    style?: StyleProp<ViewStyle>;
    onRetryUpload?: (media: FacadeMediaInfo) => void;
    onAdd?: (code: string) => void;
  }) => {
    const siteId = siteInspection.siteId;
    const { rpx } = useResponsiveContext();
    const {
      updateDetail,
      fileUploader,
      capabilities,
      navigation,
      inventoryQuery,
    } = useOrderFlowContext();
    const readonly = capabilities?.preInspectionReadonly;
    const { containerWidth, interacted$ } = useFacadeInspectionContext();
    const numColumns = 4;
    const [cellSize] = computeGridLayoutItemSize({
      numberOfColumns: numColumns,
      aspectRatio: 1,
      cellMargin: rpx(5),
      gridPadding: rpx(10),
      gridWidth: containerWidth + rpx(10),
    });
    const medias = useMemo(
      () =>
        siteInspection.items.flatMap(item =>
          item.medias.map<FacadeMediaInfo>(x => ({
            ...mediaStagedToMediaInfo(x),
            siteId: siteInspection.siteId,
            siteCode: siteInspection.siteCode,
            siteName: siteInspection.siteName,
            timestamp: x._timestamp,
          })),
        ),
      [siteInspection],
    );
    const uploadingMediaCount = kFakeStatus
      ? 7
      : siteInspection.items
          .flatMap(x => x.medias)
          .filter(
            x =>
              x._source.type === 'local' &&
              x._source.state.status !== 'error' &&
              x._source.state.status !== 'uploaded',
          ).length;
    const errorMediaCount = kFakeStatus
      ? 1
      : siteInspection.items
          .flatMap(x => x.medias)
          .filter(
            x =>
              x._source.type === 'local' && x._source.state.status === 'error',
          ).length;

    const onDeleteMedia = usePersistFn(async (media: FacadeMediaInfo) => {
      if (
        !(await withConfirmation({
          title: '确认',
          message: `是否确认删除该${media.type === 'image' ? '图片' : '视频'}?`,
        }))
      ) {
        return;
      }

      interacted$.next(true);

      updateDetail(draft => {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const siteInspectionIndex = draft.preInspection?.findIndex(
          x => x.siteId === siteId,
        );
        if (siteInspectionIndex == null || siteInspectionIndex < 0) {
          console.error(
            'site inspection is not initialized, this must be a bug',
          );
          return;
        }
        const item = draft.preInspection?.[siteInspectionIndex].items[0];
        const index = item?.medias.findIndex(x => x._key === media.key);
        if (index == null || index < 0) {
          console.error(
            'cannot find the staged media object, this must be bug',
          );
          return;
        }
        item?.medias.splice(index, 1);
        fileUploader.abort(media.key);
        if (item?.medias.length === 0) {
          // remove this site entirely.
          draft.preInspection?.splice(siteInspectionIndex, 1);
          return;
        }
        // the result follows the photos that remain.
        const site = inventoryQuery.getSiteById(siteId);
        if (item && site) {
          applyOrderFacadeItemResult(item, site);
        }
      });
    });

    const handleAdd = usePersistFn(() => {
      onAdd?.(siteInspection.siteCode);
    });

    const onPreviewVideo = usePersistFn((media: FacadeMediaInfo) => {
      navigation.navigate('_mediaPreview', { url: media.url, type: 'video' });
    });

    return (
      <LayoutProviderView
        css={`
          margin-top: ${rpx(10)}px;
          background-color: #fff;
        `}
        style={style}
      >
        <View
          css={`
            padding-top: ${rpx(10)}px;
          `}
        >
          <HStack
            justifyContent="flex-start"
            alignItems="center"
            css={`
              border-left-width: ${rpx(10)}px;
              border-left-color: rgba(48, 117, 221, 1);
              padding-left: ${rpx(10)}px;
            `}
          >
            <Typography.Label medium size={rpx(18)} color={'#333'}>
              {siteInspection.siteName}
            </Typography.Label>
            <Typography.Label
              size={rpx(18)}
              light
              color="#aaa"
              css={`
                margin-left: ${rpx(6)}px;
              `}
            >
              {medias.length}
            </Typography.Label>
            {medias.length > 0 && !readonly ? (
              <HStack
                justifyContent="flex-end"
                alignItems="center"
                css={`
                  flex: 1;
                `}
              >
                {uploadingMediaCount > 0 ? (
                  <ActivityIndicator
                    size="small"
                    css={`
                      transform: scale(0.75);
                    `}
                  />
                ) : null}
                <Typography.Label
                  size={rpx(14)}
                  family={
                    errorMediaCount > 0 && uploadingMediaCount === 0
                      ? 'bold'
                      : 'regular'
                  }
                  color={
                    uploadingMediaCount > 0
                      ? '#666'
                      : errorMediaCount > 0
                      ? StatusColors.Danger
                      : StatusColors.Success
                  }
                  css={`
                    text-align: right;
                    margin-right: ${rpx(10)}px;
                  `}
                >
                  {uploadingMediaCount > 0
                    ? `上传中, ${uploadingMediaCount}张剩余... `
                    : errorMediaCount > 0
                    ? `${errorMediaCount}上传失败`
                    : `上传成功`}
                </Typography.Label>
              </HStack>
            ) : null}
          </HStack>
        </View>
        <FacadeMediaList
          editable={!readonly}
          medias={medias}
          width={containerWidth}
          numColumns={numColumns}
          cellSize={cellSize}
          cellMargin={rpx(5)}
          removeIconSize={rpx(16)}
          hideLabel={true}
          scrollEnabled={false}
          onDelete={onDeleteMedia}
          onRetryUpload={onRetryUpload}
          onPreviewVideo={onPreviewVideo}
          onAdd={handleAdd}
          contentContainerStyle={{
            padding: rpx(5),
          }}
        />
      </LayoutProviderView>
    );
  },
);
